"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _dcCore = require("dc-core");
var _CommentsUtils = require("../util/CommentsUtils");
var _ServiceRegistry = _interopRequireDefault(require("../util/ServiceRegistry"));
var _ApiConstants = _interopRequireDefault(require("../constants/ApiConstants"));
var _CallbackTypes = _interopRequireDefault(require("../constants/CallbackTypes"));
var _Events = require("../constants/Events");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); } /*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2020 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
/**
 * @class
 * @description
 * The class provides all annotation related methods and handle interaction with comments dropin
 */
class CommentsApi {
  constructor() {
    this.listeners = [];
    this.initApis();
  }
  initApis() {
    this._commentsApis = {};
    this._commentApiPromise = new Promise(resolve => {
      this._commentApiResolve = resolve;
    });
    this._commentApiPromise.then(() => {
      if (this.isSharedCommentingMode()) {
        this._commentsApis.registerEventListener((event, data) => {
          if (event === _Events.AnnotationEvents.ANNOTATION_MODE_STARTED && !_dcCore.auth2.isSignedIn) {
            _ServiceRegistry.default.getService("AppStore").setLastAnnotationToolClicked(data);
            _dcCore.auth2.signIn();
            this._commentsApis.endAnnotationMode();
          }
        });
      }
    });
  }
  resetApis() {
    this.initApis();
  }

  /**
   * @method
   * @description
   * it exposes interface that can be accessed in comment dropin
   *
   * @return {Object} List of all exposed comments dropin apis.
   */
  getExposedAPIs() {
    return {
      areAnnotationAPIsEnabled: () => _ServiceRegistry.default.getService("AppStore").getActionConfig("preview").enableAnnotationAPIs,
      includePDFAnnotations: () => _ServiceRegistry.default.getService("AppStore").getActionConfig("preview").enableAnnotationAPIs ? _ServiceRegistry.default.getService("AppStore").getActionConfig("preview").includePDFAnnotations : true,
      registerCommentsApis: this.registerCommentsApis.bind(this),
      isFreetextEnabled: this.isFreetextEnabled.bind(this),
      isEraserToolEnabled: this.isEraserToolEnabled.bind(this),
      registerTools: this.registerTools.bind(this),
      isSharedCommentingMode: this.isSharedCommentingMode.bind(this),
      isSharedFileMode: this.isSharedFileMode.bind(this),
      isReadOnlyMode: () => !_ServiceRegistry.default.getService("AppStore").isEditAllowed,
      setPreventDirtyPDF: this.setPreventDirtyPDF.bind(this),
      isMentionEnabled: () => _ServiceRegistry.default.getService("CallbackService").hasCallback(_CallbackTypes.default.GET_SUGGESTION_LIST_API),
      getSuggestionList: this.getSuggestionList.bind(this),
      shouldShowPinTool: this.shouldShowPinTool.bind(this)
    };
  }

  /**
   * @method
   * @description
   * Interface for registering tool (comments dropin registers topbar tools and toolbar)
   *
   * @param {Object} actions comment tools actions
   */
  registerTools(actions) {
    this.tools = actions;
  }

  /**
   * @method
   * @description
   * it provides all the available top bar JSON based tools registered by comment dropin
   *
   * @return {Object} all the availble top bar JSON tools
   */
  getTopBarTools() {
    return this.tools ? this.tools.getTopBarTools() : [];
  }

  /**
   * @method
   * @description
   * it provides all the available top bar React based Custom tools registered by comment dropin
   *
   * @return {Object} all the availble top bar React Custom tools
   */
  getCustomTopBarTools() {
    return this.tools && this.tools.getCustomTopBarTools ? this.tools.getCustomTopBarTools() : [];
  }

  /**
   * @method
   * @description
   * it provides whole toolbar react component (registered by comments dropin)
   *
   * @return react component of toolbar
   */
  getToolbar() {
    return this.tools ? this.tools.getToolbar() : /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null);
  }

  /**
   * @method
   * @description
   * it checks whether Free text annotation is allowed or not
   *
   * @return {Boolean} is Free text annotation allowed
   */
  isFreetextEnabled() {
    return _ServiceRegistry.default.getService("AppStore").getActionConfig("preview").enableFreeTextAnnotationTool;
  }

  /**
   * @method
   * @description
   * it checks whether to allow eraser annotation tool
   *
   * @return {Boolean} is eraser tool allowed
   */
  isEraserToolEnabled() {
    return _ServiceRegistry.default.getService("AppStore").getActionConfig("preview").enableEraserAnnotationTool;
  }
  setPreventDirtyPDF(value) {
    _ServiceRegistry.default.getService("AppStore").preventDirtyPDF = value;
  }

  /**
   * @method
   * @description
   * Return the array of people that can be mentioned
   *
   * @param String query use to filter list
   */
  getSuggestionList(query) {
    const cbService = _ServiceRegistry.default.getService("CallbackService");
    const getSuggestionCallback = cbService.getCallback(_CallbackTypes.default.GET_SUGGESTION_LIST_API);
    return new Promise(resolve => getSuggestionCallback(query).then(resolve).catch(() => resolve([])));
  }

  /**
   * @method
   * @description
   * Checks whether shared commenting is supported or not
   *
   */
  isSharedCommentingMode() {
    return _ServiceRegistry.default.getService("AppStore").getActionConfig("preview").sharedCommentingMode;
  }

  /**
   * @method
   * @description
   * Checks whether sharedfilemode is on
   */
  isSharedFileMode() {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    return appStore.getActionConfig("preview").isSharedFileMode && appStore.fileMetadata.isShared;
  }

  /**
   * @method
   * @description
   * Checks whether pin tool should be shown
   */
  shouldShowPinTool() {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    return appStore.getActionConfig("preview").pinToolConfig;
  }

  /**
   * @method
   * @description
   * it registers interface provided by comment dropin component to invoke its methods
   * and take care of any configuration which needs to be executed after registeration.
   *
   * @param {Object} commentsApi interface registered by comment dropin
   */
  registerCommentsApis(commentsApi) {
    this._commentsApis = commentsApi;
    const config = _ServiceRegistry.default.getService("AppStore").getActionConfig("preview");
    if (config.enableAnnotationAPIs) {
      this.setConfig(config.annotationUIConfig);
    } else {
      if (config.annotationUIConfig && "showCommentsPanel" in config.annotationUIConfig) {
        this._commentsApis.setConfig({
          showCommentsPanel: config.annotationUIConfig.showCommentsPanel
        });
      }
      this.registerCommentsEventListener((event, data) => {
        let eventData = data;
        if (data.motivation) {
          eventData = data;
        }
        _ServiceRegistry.default.getService("EventsService").sendEvent(event, eventData);
      });
    }
    this.registerCallbackForDownloadPrint();
    this._commentApiResolve();
  }

  /**
   * @method
   * @description
   * it register event listener provided by comment dropin component
   *
   * @param {Object} listener function listened to event.
   */
  registerCommentsEventListener(listener) {
    return this._commentApiPromise.then(() => {
      this.listeners.push(listener);
      return this._commentsApis.registerEventListener(listener);
    });
  }

  /**
   * @method
   * @description
   * it removes all the registered event listeners
   *
   */
  removeCommentsEventListeners() {
    return this.listeners.forEach(listener => this._commentsApis.removeEventListener(listener));
  }
  toggleCommenting(bool, options) {
    return this._commentApiPromise.then(() => this._commentsApis.toggleCommenting(bool, options));
  }

  /**
   * @method
   * @description
   * it provides the interface to add annotations using apis provided by comment dropin component
   *
   * @param {Object} annotations.
   *
   * @param options
   * @return {Promise} Promise resolved when successful and reject with error.
   */
  async addAnnotations(annotations, options = {}) {
    const currentAnnots = await this.getAnnotations({});
    const existingIds = currentAnnots.map(annot => annot.id);
    let invalidAnnots = annotations.filter(annot => existingIds.indexOf(annot.id) !== -1);
    if (invalidAnnots.length > 0) {
      return Promise.reject(new Error(`Annotation already there for given id ${invalidAnnots.map(annot => annot.id).join(", ")}`));
    }
    const appStore = _ServiceRegistry.default.getService("AppStore");
    if (appStore.pdfMetadata && appStore.pdfMetadata.numPages && annotations.filter(annotation => annotation.motivation === "commenting").length > 0) {
      const pageBoundaries = await appStore.previewRef.getPublicApis().then(previewApis => Promise.all([...Array(appStore.pdfMetadata.numPages).keys()].map(index => previewApis.document.getPageAttributes(index + 1).then(attr => attr.boxes.CropBox))));
      invalidAnnots = annotations.filter(annot => annot.motivation === "replying" ? false : !(0, _CommentsUtils.isValidSelector)(pageBoundaries[annot.target.selector.node.index], annot));
      if (invalidAnnots.length > 0) {
        return Promise.reject(new Error(`Annotation selector is not valid for given ${invalidAnnots.map(annot => annot.id).join(", ")}`));
      }
    }
    const shouldTakeFocus = "silent" in options ? !options.silent : !(annotations.length > 1);
    const showCommentsPanel = appStore.configStore.showCommentsPanel;
    if (showCommentsPanel && shouldTakeFocus) {
      await this.toggleCommentsPanel(true);
    }
    const opts = {
      shouldTakeFocus
    };
    const result = [];
    for (let i = 0; i < annotations.length; i += 1) {
      let annotationObject = (0, _CommentsUtils.createAnnotationObject)(annotations[i]);
      if (annotationObject.motivation === "replying") {
        // eslint-disable-next-line no-await-in-loop
        result.push(await this._commentsApis.addReply(annotationObject, opts));
      } else {
        if (annotationObject.target.selector.subtype === "freetext") {
          annotationObject = (0, _CommentsUtils.normalizeFreeTextAnnotation)(annotationObject);
        }
        if (annotationObject.target.selector.subtype === "shape") {
          annotationObject = (0, _CommentsUtils.normalizeShapeAnnotation)(annotationObject);
        }
        // eslint-disable-next-line no-await-in-loop
        result.push(await this._commentsApis.addComment(annotationObject, opts));
      }
    }
    return Promise.all(result).then(() => Promise.resolve());
  }

  /**
   * @method
   * @description
   * it provides the interface to list down all the annotations with specific filters using
   * apis provided by comment dropin component
   *
   * @param {Object} filters.
   *
   * @return {Promise} Promise resolved with all annotations.
   */
  getAnnotations(filters) {
    return this._commentApiPromise.then(() => this._commentsApis.getComments((0, _CommentsUtils.modifyFilter)(filters))).then(annotations => annotations.map(annotation => (0, _CommentsUtils.createAnnotationObject)((0, _CommentsUtils.transformAnnotation)(annotation))));
  }

  /**
   * @method
   * @description
   * it provides the interface to delete annotations on basis of filter provided using
   * apis provided by comment dropin component
   *
   * @param {Object} filters.
   *
   * @return {Promise} Promise resolved when successful and reject with error.
   */
  deleteAnnotations(filters) {
    return this._commentApiPromise.then(() => this._commentsApis.deleteComments((0, _CommentsUtils.modifyFilter)(filters)));
  }

  /**
   * @method
   * @description
   * it provides the interface to update the annotation using apis provided
   * by comment dropin component
   *
   * @param {Object} annotation annotation need to be updated
   *
   * @return {Promise} Promise resolved when successful and reject with error.
   */
  async updateAnnotation(annotation) {
    let annot = annotation;
    const appStore = _ServiceRegistry.default.getService("AppStore");
    if (annot.motivation === "commenting") {
      if (appStore.pdfMetadata && appStore.pdfMetadata.numPages) {
        const pageBoundaries = await appStore.previewRef.getPublicApis().then(previewApis => Promise.all([...Array(appStore.pdfMetadata.numPages).keys()].map(index => previewApis.document.getPageAttributes(index + 1).then(attr => attr.boxes.CropBox))));
        const invalidAnnot = (0, _CommentsUtils.isValidSelector)(pageBoundaries[annot.target.selector.node.index], annot);
        if (!invalidAnnot) {
          return Promise.reject(new Error(`Annotation selector is not valid for given ${annot.id}`));
        }
      }
      if (annot.target.selector.subtype === "freetext") {
        annot = (0, _CommentsUtils.normalizeFreeTextAnnotation)(annot);
      }
      if (annot.target.selector.subtype === "shape") {
        annot = (0, _CommentsUtils.normalizeShapeAnnotation)(annot);
      }
    }
    return this._commentApiPromise.then(() => this._commentsApis.updateComment(annot));
  }

  /**
   * @method
   * @description
   * it provides the interface to select the annotation using apis provided
   * by comment dropin component
   *
   * @param id id of annotation
   */
  selectAnnotation(id) {
    return this._commentApiPromise.then(() => this._commentsApis.selectComment(id));
  }

  /**
   * @method
   * @description
   * it provides the interface to unselect the annotation using apis provided
   * by comment dropin component
   * The last selected annotation will be unselected if any.
   */
  unselectAnnotation() {
    return this._commentApiPromise.then(() => this._commentsApis.unselectComment());
  }

  /**
   * @method
   * @description
   * It will commit all the unsaved/non-committed changes in comments dropin
   */
  commitUnsavedChanges() {
    return this._commentApiPromise.then(() => {
      if (typeof this._commentsApis.commitUnsavedChanges === "function") {
        return this._commentsApis.commitUnsavedChanges();
      }
      return new Promise(res => setTimeout(res, 200));
    });
  }

  /**
   * @method
   * @description
   * it provides the interface to enter in annotation mode using apis provided
   * by comment dropin component
   *
   * @param {String} mode mode of annotations
   * @param {Object} options options can be deafult color and cursor.
   */
  startAnnotationMode(mode, options) {
    return this._commentApiPromise.then(() => {
      this._commentsApis.startAnnotationMode(mode, options);
      if (options.cursor) {
        _ServiceRegistry.default.getService("AppStore").previewRef.getApis("INT_COMMANDS", ["modifyCursor"]).then(api => api.modifyCursor.setCursor(options.cursor));
      }
    });
  }

  /**
   * @method
   * @description
   * it provides the interface to end the last started annotation mode using apis provided
   * by comment dropin component
   */
  endAnnotationMode() {
    return this._commentApiPromise.then(() => {
      this._commentsApis.endAnnotationMode();
      _ServiceRegistry.default.getService("AppStore").previewRef.getApis("INT_COMMANDS", ["modifyCursor"]).then(api => api.modifyCursor.resetCursor());
    });
  }
  setConfig(config) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    return this._commentApiPromise.then(() => {
      this._commentsApis.setConfig(config);
      if ("showToolbar" in config) {
        appStore.configStore.setShowCommentingToolbar(config.showToolbar);
        appStore.previewRef.getPublicApis().then(apis => {
          const customizeUIControl = apis.app.customizeUIControl;
          customizeUIControl("COMMENT_STICKYNOTE_CONTROL", {
            visible: config.showToolbar,
            containerIDs: [_ApiConstants.default.CUSTOMIZABLE_PANEL.QUICK_TOOLS]
          });
          if (this.isFreetextEnabled()) {
            customizeUIControl("COMMENT_TEXT_CONTROL", {
              visible: config.showToolbar,
              containerIDs: [_ApiConstants.default.CUSTOMIZABLE_PANEL.QUICK_TOOLS]
            });
          }
          customizeUIControl("COMMENT_HIGHLIGHT_CONTROL", {
            visible: config.showToolbar,
            containerIDs: [_ApiConstants.default.CUSTOMIZABLE_PANEL.QUICK_TOOLS]
          });
          customizeUIControl("COMMENT_UNDERLINE_CONTROL", {
            visible: config.showToolbar,
            containerIDs: [_ApiConstants.default.CUSTOMIZABLE_PANEL.QUICK_TOOLS]
          });
          customizeUIControl("COMMENT_STRIKEOUT_CONTROL", {
            visible: config.showToolbar,
            containerIDs: [_ApiConstants.default.CUSTOMIZABLE_PANEL.QUICK_TOOLS]
          });
          customizeUIControl("COMMENT_DRAW_CONTROL", {
            visible: config.showToolbar,
            containerIDs: [_ApiConstants.default.CUSTOMIZABLE_PANEL.QUICK_TOOLS]
          });
        });
      }
      if ("showCommentsPanel" in config) {
        appStore.configStore.setShowCommentsPanel(config.showCommentsPanel);
        appStore.previewRef.getPublicApis().then(apis => {
          apis.app.customizeUIControl("COMMENTS_PANEL", {
            visible: config.showCommentsPanel
          });
          this.toggleCommentsPanel(config.showCommentsPanel);
        });
      }
      if ("downloadWithAnnotations" in config) {
        this.downloadWithAnnotations = config.downloadWithAnnotations;
      }
      if ("printWithAnnotations" in config) {
        this.printWithAnnotations = config.printWithAnnotations;
      }
    });
  }
  toggleCommentsPanel(bool) {
    return this._commentApiPromise.then(() => {
      this._commentsApis.stores.commentsDropinStore.setShouldShowCommentsPanel(bool);
    });
  }

  /**
   * @method
   * @description
   * it provides the interface to registering the callback for user action like download and
   * print pdf using apis provided by comment dropin component
   */
  registerCallbackForDownloadPrint() {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    if (!appStore.getActionConfig("preview").enableAnnotationAPIs || appStore.getActionConfig("preview").includePDFAnnotations) {
      return;
    }
    ["Download", "Print"].forEach(action => {
      appStore.previewRef.getPublicApis().then(previewApis => {
        previewApis.app[`before${action}`](() => {
          appStore.preventDirtyPDF = true;
          return this._commentsApis.beforePrintDownload(this[`${action.toLowerCase()}WithAnnotations`]);
        });
        previewApis.app[`after${action}`](() => {
          this._commentsApis.afterPrintDownload(this[`${action.toLowerCase()}WithAnnotations`]).then(() => {
            appStore.preventDirtyPDF = false;
          });
        });
      });
    });
  }

  /**
   * @method
   * @description
   * it provides the interface to add/burn annotations in PDF buffer using apis provided
   * by comment dropin component.
   *
   * @param annotations list of annotation data
   *
   * @return the updated PDF buffer
   */
  addAnnotationsInPDF(annotations) {
    if (!_ServiceRegistry.default.getService("AppStore").getActionConfig("preview").includePDFAnnotations) {
      return Promise.reject(new Error("includePDFAnnotations should be set to true"));
    }
    return this.addAnnotations(annotations).then(() => _ServiceRegistry.default.getService("AppStore").previewRef.getApis("INTEGRATION_CONNECTOR", ["document"])).then(apiObject => apiObject.document.getUpdatedPDFBuffer(false));
  }

  /**
   * @method
   * @description
   * it provides the interface to delete annotations from PDF buffer using apis provided
   * by comment dropin component.
   *
   * @return {Object} return JSON object of the PDF buffer and anootations
   */
  removeAnnotationsFromPDF() {
    if (!_ServiceRegistry.default.getService("AppStore").getActionConfig("preview").includePDFAnnotations) {
      return Promise.reject(new Error("includePDFAnnotations should be set to true"));
    }
    let annotations;
    return this.getAnnotations({}).then(annots => {
      annotations = annots;
      return annots.length > 0 ? this.deleteAnnotations({}) : Promise.resolve();
    }).then(() => _ServiceRegistry.default.getService("AppStore").previewRef.getApis("INTEGRATION_CONNECTOR", ["document"])).then(apiObject => apiObject.document.getUpdatedPDFBuffer(false)).then(pdfBuffer => ({
      pdfBuffer,
      annotations
    }));
  }

  /**
   * @method
   * @description
   * it provides the interface to get current PDF buffer with or without merged anotations using apis provided
   * by comment dropin component.
   *
   * @return {Object} return the PDF buffer
   */
  getPDFBuffer(options) {
    const appStore = _ServiceRegistry.default.getService("AppStore");
    if (!options.mergeAnnotations) {
      return appStore.previewRef.getApis("INTEGRATION_CONNECTOR", ["document"]).then(apiObject => apiObject.document.getUpdatedPDFBuffer(false));
    }
    return this.getAnnotations({}).then(annots => {
      annots.forEach(annot => {
        if (annot.target.selector.subtype === "freetext") {
          // eslint-disable-next-line no-param-reassign
          annot = (0, _CommentsUtils.normalizeFreeTextAnnotation)(annot);
        }
      });
      const comments = annots.filter(annot => annot.motivation === "commenting");
      const replies = annots.filter(annot => annot.motivation === "replying");
      return comments.map(comment => _objectSpread(_objectSpread({}, comment), {}, {
        replies: replies.filter(reply => comment.id === reply.target.source)
      }));
    }).then(async annots => {
      appStore.preventDirtyPDF = true;
      // when we mergeAnnotations we need to clear the annots from buffer to remove duplicates.
      const pdfAnnots = await this._commentsApis.removeAnnotationsFromPDFBuffer();
      return this._commentsApis.addAnnotationsInPDF(annots).then(() => appStore.previewRef.getApis("INTEGRATION_CONNECTOR", ["document"]).then(apiObject => apiObject.document.getUpdatedPDFBuffer(false)).then(buffer => {
        const removeAnnotationsPromise = this._commentsApis.removeAnnotationsFromPDF(annots);
        // Once buffer is retrieved, put buffer annotations back into PDF
        const addAnnotationsPromise = pdfAnnots ? this._commentsApis.addAnnotationsInPDF(pdfAnnots) : Promise.resolve();
        Promise.all([removeAnnotationsPromise, addAnnotationsPromise]).then(() => {
          appStore.preventDirtyPDF = false;
        });
        return buffer;
      }));
    });
  }
}
var _default = exports.default = CommentsApi;